import React from 'react';
import { Button, Form, Input, Select } from 'antd';

import { extractApiErrorMessage, openNotification, toTurkishUpper } from '../../../utils';
import { createDistrict, getDistrict, updateDistrict } from '../services/districtService';
import type { CreateDistrictDto } from '../types/District';
import { getState, listStatesByActive } from '../../states/services/stateService';
import type { State } from '../../states/types/State';

type FormValues = {
  name: string;
  stateId?: number;
};

const dedupeStates = (items: State[]) => {
  const map = new Map<number, State>();
  items.forEach((item) => {
    if (typeof item?.id === 'number') {
      map.set(item.id, item);
    }
  });
  return Array.from(map.values()).sort((a, b) => String(a?.name ?? '').localeCompare(String(b?.name ?? ''), 'tr'));
};

export default function DistrictForm({ id, onSuccess }: { id?: number; onSuccess: () => void }) {
  const [form] = Form.useForm<FormValues>();
  const [stateOptions, setStateOptions] = React.useState<State[]>([]);

  React.useEffect(() => {
    const load = async () => {
      try {
        const activeStates = await listStatesByActive(true);
        let nextStates = Array.isArray(activeStates) ? activeStates : [];

        if (id) {
          const district = await getDistrict(id);

          if (typeof district?.stateId === 'number') {
            const currentState = await getState(district.stateId);
            if (!nextStates.some((item) => item.id === currentState.id)) {
              nextStates = dedupeStates([...nextStates, currentState]);
            }
          }

          form.setFieldsValue({
            name: district?.name ?? '',
            stateId: district?.stateId,
          });
        } else {
          form.resetFields();
        }

        setStateOptions(dedupeStates(nextStates));
      } catch (error) {
        const message = extractApiErrorMessage(error, 'İlçe formu yüklenemedi');
        openNotification('error', 'Hata', message);
      }
    };

    load();
  }, [id, form]);

  const onFinish = async (values: FormValues) => {
    const payload: CreateDistrictDto = {
      name: toTurkishUpper(values.name)?.trim() ?? '',
      stateId: Number(values.stateId),
    };

    try {
      if (id) {
        await updateDistrict(id, payload);
        openNotification('success', 'Başarılı', 'İlçe güncellendi');
      } else {
        await createDistrict(payload);
        openNotification('success', 'Başarılı', 'İlçe eklendi');
      }
      onSuccess();
    } catch (error) {
      const message = extractApiErrorMessage(error, 'İlçe işlemi başarısız');
      openNotification('error', 'Hata', message);
    }
  };

  return (
    <Form form={form} layout='vertical' onFinish={onFinish}>
      <Form.Item name='name' label='İlçe Adı' rules={[{ required: true, message: 'İlçe adı zorunludur' }]}>
        <Input style={{ textTransform: 'uppercase' }} lang='tr' />
      </Form.Item>
      <Form.Item name='stateId' label='İl' rules={[{ required: true, message: 'İl seçiniz' }]}>
        <Select
          showSearch
          placeholder='İl seçiniz'
          optionFilterProp='children'
          filterOption={(input, option) =>
            String(option?.children ?? '').toLocaleLowerCase('tr-TR').includes(input.toLocaleLowerCase('tr-TR'))
          }
        >
          {stateOptions.map((state) => (
            <Select.Option key={state.id} value={state.id}>
              {state.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Button htmlType='submit' type='primary' block>
        Kaydet
      </Button>
    </Form>
  );
}
